import Combobox from './combobox';

/**
 * Custom select dropdown that mirrors native select input. Provides following features:
 *
 * - Extends {@link Combobox} base class which accepts native select element as parameter. See combobox class for more documentation.
 * - Implements keyboard functionality based on ARIA Select-only combobox
 * - Search that activates first matching element and repeating searches cycle through all matches
 *
 * @see {@link https://www.w3.org/WAI/ARIA/apg/patterns/combobox/examples/combobox-select-only/}
 */
class DropdownCombobox extends Combobox {

  #search = {
    currentSearchPhrase: '',
    searchResultIndex: 0,
    lastSearchPhrase: '',
    lastSearchTimeout: null
  };

  /**
   * Create new dropdown combobox.
   *
   * @param {ComboboxOptions} options Dropdown options.
   * @param {number} [options.searchDelay=300] Amount of milliseconds to wait for input before search is run.
   * @param {function(string, HTMLElement[]):string[]} [onSearch] Overrides core search logic and returns array of option values that match given search.
   */
  constructor(options = {}) {
    options = {
      searchDelay: 300,
      ...(options || {})
    };
    super(options);
  }

  /**
   * @inheritdoc
   * @protected
   */
  createElements() {
    const elements = super.createElements();
    const config = this.getConfig();

    const container = document.createElement('div');
    container.id = this.getId();
    container.classList.add(config.cssContainerClass);

    const button = document.createElement('button');
    button.type = 'button';
    button.innerText = this.getConfig().placeholder;
    button.id = this.getId() + '-control';
    button.classList.add(config.cssControlClass);
    button.setAttribute('role', 'combobox');
    button.setAttribute('aria-haspopup', 'listbox');
    button.setAttribute('aria-expanded', 'false');
    button.setAttribute('aria-labelledby', elements?.label?.id || '');
    //button.setAttribute('aria-owns', elements.list.id);
    //button.setAttribute('aria-controls', this.getId() + '-control-content');
    button.setAttribute('aria-controls', elements.list.id);
    button.disabled = config.disabled;

    elements.list.setAttribute('aria-labelledby', elements?.label?.id || '');

    container.appendChild(button);
    container.appendChild(elements.list);

    return {
      ...elements,
      container,
      control: button
    };
  }

  /**
   * @inheritdoc
   * @protected
   */
  updateSelection(content, data) {
    this.getElements().control.innerHTML = content || this.getConfig().placeholder;
  }

  /**
   * Search matching item based on current search phrase.
   *
   * @param {KeyboardEvent} e Triggered event.
   * @protected
   */
  onKeyUp(e) {
    let search = this.#search.currentSearchPhrase;
    if (e.key.length === 'Backspace') {
      this.#search.currentSearchPhrase = search.slice(0, -1);
    }
    else if (e.key.length === 1 && e.key !== ' ') {
      this.#search.currentSearchPhrase = search + e.key;
    }
    else {
      return;
    }

    if (this.#search.lastSearchTimeout) {
      clearTimeout(this.#search.lastSearchTimeout);
    }

    this.#search.lastSearchTimeout = setTimeout(() => {
      this.search(this.#search.currentSearchPhrase);
      this.#search.lastSearchPhrase = this.#search.currentSearchPhrase;
      this.#search.lastSearchTimeout = null;
      this.#search.currentSearchPhrase = '';
    }, this.getConfig().searchDelay);
  }

  /**
   * Changes active item based on keypress.
   *
   * @param {string} key Pressed key.
   */
  #handleActiveItemChange(key) {
    const options = this.getSelectableItems();
    if (options.length === 0) {
      return;
    }

    if (key === 'Home') {
      this.setActiveItem(options[0], true, 'top');
      return;
    }
    else if (key === 'End') {
      this.setActiveItem(options[options.length - 1], true, 'bottom');
      return;
    }

    const positions = {
      'ArrowUp': -1,
      'PageUp': -10,
      'ArrowDown': 1,
      'PageDown': 10
    };
    const position = positions[key];

    const option = this.findSelectableItem(this.getActiveItem() || options[0], position);
    if (option) {
      this.setActiveItem(option, true, position < 0 ? 'top' : 'bottom');
    }
  }

  /**
   * Handles key events.
   *
   * @param {KeyboardEvent} e Triggered event.
   * @protected
   */
  onKeyDown(e) {
    const map = {
      ' ': 'Space',
      'Esc': 'Escape'
    };
    const actionKey = map[e.key] || e.key;

    if (!this.isOpen()) {
      switch(actionKey) {
      case 'Enter':
      case 'Space':
      case 'ArrowDown': {
        e.preventDefault();
        this.open(true);
        return;
      }
      case 'ArrowUp':
      case 'Home':
      case 'End': {
        e.preventDefault();
        this.open(true);
        this.#handleActiveItemChange(actionKey === 'ArrowUp' ? 'Home' : actionKey);
        return;
      }
      }
    }
    else {
      switch(actionKey) {
      case 'Escape': {
        e.preventDefault();
        this.close(true);
        return;
      }
      case 'Enter':
      case 'Space': {
        e.preventDefault();

        const activeOption = this.getActiveItem();
        if (activeOption) {
          this.toggleValue(activeOption.dataset.value);

          if (!this.isMultiSelect()) {
            this.close(true);
          }
        }
        return;
      }
      case 'ArrowDown':
      case 'ArrowUp':
      case 'PageUp':
      case 'PageDown':
      case 'Home':
      case 'End': {
        e.preventDefault();
        this.#handleActiveItemChange(actionKey);
        return;
      }
      }
    }
  }



  /**
   * Search item which text starts with given search phrase. Activates first match,
   * repeating searches with same search phrase will cycle through matches.
   *
   * @param {string} search Search phrase.
   */
  search(search) {
    const searchItems = this.getSelectableItems();

    let result = this.getConfig()?.onSearch?.call(this, search, searchItems) || null;
    if (result === null) {
      result = searchItems.filter(option => !search || option.dataset.text.toLowerCase().indexOf(search.toLowerCase()) === 0).map(item => item.dataset.value);
    }

    if (result.length === 0) {
      return;
    }

    // Set repeat amount for search phrase
    if (search !== this.#search.lastSearchPhrase) {
      this.#search.searchResultIndex = 0;
    }
    else {
      this.#search.searchResultIndex++;
    }

    let index = this.#search.searchResultIndex;

    // Set to 0 if only one result is found
    if (index > 0 && !result[index]) {
      index = 0;
      this.#search.searchResultIndex = 0;
    }

    result = result[index];

    const activeItem = searchItems.find(item => result === item.dataset.value);
    if (activeItem) {
      this.open(true);
      this.setActiveItem(activeItem);
    }
  }

}

export default DropdownCombobox;
